import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { api } from "../utils/api";
import storage from "../assets/js/Global";
import { appconfig, urls } from "../urls/Url";
import { Oval } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import { images } from "../assets/images";
import { requestForToken } from "../fcm";

export const GlobalContext = createContext();

const GlobalProvider = ({ children }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [userData, setUserData] = useState({
        email: '',
        password: '',
        otp: '',
        setPassword: '',
        confirmPassword: ''
    });
    const [preview, setPreview] = useState(images.userAvatar);
    const [profileData, setProfileData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        dob: "",
        gender: "",
    });
    const [content, setContent] = useState({});
    const [chatList, setChatList] = useState([]);
    const [employeeList, setEmployeeList] = useState([]);
    const [jobsList, setJobsList] = useState([]);
    const [jobDetail, setJobDetail] = useState(null);
    const [courseList, setCourseList] = useState([]);
    const [unreadMessages, setUnreadMessages] = useState(0);
    const navigate = useNavigate();
    const deviceId = storage.get('device_id');
    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    const isLoggedIn = storage.get("isLoggedIn");
    let loginObj = storage.get('user');
    const isValidEmail = (email) => {
        return EMAIL_REGEX.test(String(email).toLowerCase());
    };

    const getBrowserName = () => {
        const userAgent = navigator.userAgent;
        if (userAgent.includes("Chrome")) return "Chrome";
        if (userAgent.includes("Firefox")) return "Firefox";
        if (userAgent.includes("Safari")) return "Safari";
        return "Unknown";
    };

    const getOS = () => {
        const userAgent = navigator.userAgent;
        if (userAgent.includes("Windows")) return "Windows";
        if (userAgent.includes("Mac")) return "MacOS";
        if (userAgent.includes("Linux")) return "Linux";
        return "Unknown";
    };

    const getOSVersion = () => {
        const ua = navigator.userAgent;

        let match = ua.match(/Windows NT (\d+\.\d+)/);
        if (match) return `Windows ${match[1]}`;

        match = ua.match(/Mac OS X (\d+[._]\d+[._]?\d*)/);
        if (match) return `MacOS ${match[1].replace(/_/g, ".")}`;

        match = ua.match(/Android (\d+(\.\d+)?)/);
        if (match) return `Android ${match[1]}`;

        match = ua.match(/OS (\d+[_\d]*) like Mac OS X/);
        if (match) return `iOS ${match[1].replace(/_/g, ".")}`;

        if (ua.includes("Linux")) return "Linux";

        return "Unknown";
    };

    const postApi = async (url, payload, skipAuth = false, skipLoader = false, customHeaders = {}) => {
        if (!skipLoader) setIsLoading(true);

        try {
            const headers = { ...customHeaders };

            if (payload instanceof FormData) {
                if (headers["Content-Type"]) delete headers["Content-Type"];
                headers["Content-Type"] = undefined;
            }

            const config = {
                headers,
                skipAuth,
            };

            const response = await api.post(url, payload, config);
            return response.data;

        } catch (error) {
            return error?.response?.data || {
                status: false,
                err: "Something went wrong"
            };

        } finally {
            if (!skipLoader) setIsLoading(false);
        }
    };

    const getDeviceType = () => {
        const ua = navigator.userAgent;

        if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) {
            return "mobile";
        }

        if (/Tablet|iPad/i.test(ua)) {
            return "tablet";
        }

        return "desktop";
    };

    const getAppType = () => {
        const ua = navigator.userAgent;

        if (/wv|WebView/i.test(ua)) {
            return "webview";
        }

        return "desktop_web";
    };

    const getDeviceId = async () => {
        const payload = {
            app_type: getAppType(),
            cpu: navigator.hardwareConcurrency || 6,
            ram: navigator.deviceMemory ? navigator.deviceMemory + " GB" : "16 GB",
            browser_name: getBrowserName(),
            host: window.location.host,
            screen_size: `${window.screen.width}x${window.screen.height}`,
            font_scale: 1,
            device_type: getDeviceType(),
            os_provider: getOS(),
            os_version: getOSVersion(),
            platform: navigator.platform,
            app_build_version: appconfig.appVersion,
            tz_name: Intl.DateTimeFormat().resolvedOptions().timeZone,
            tz_offset: new Date().getTimezoneOffset(),
            network_type: navigator.connection?.effectiveType || "4g",
            downlink: navigator.connection?.downlink ? `${navigator.connection.downlink} Mbps` : "10 Mbps",
            network_rtt: navigator.connection?.rtt ? `${navigator.connection.rtt} ms` : "100 ms"
        };

        try {
            const response = await postApi(urls.deviceRegister, payload, true, true);
            const deviceId = response.deviceID;
            storage.set("device_id", deviceId);
            return deviceId;
        } catch (error) {
            console.error("Failed to get device ID", error);
        }
    };

    const getRefreshToken = useCallback(async () => {
        const refresh_token = storage.get("refreshToken");
        const jti = storage.get("jti");
        if (!refresh_token) throw new Error("No refresh token found");

        const payload = {
            refresh: refresh_token,
            jti,
            username: loginObj?.email
        };

        const response = await postApi(urls.getRefreshToken, payload, false, true);

        if (response?.status === true) {
            const token = response?.jwt;

            if (!token || typeof token !== "object") {
                toast.error("Invalid token received");
                return;
            }

            const authData = {
                accessToken: token?.access,
                refreshToken: token?.refresh,
                refresh_exp: token?.refresh_exp,
                access_exp: token?.access_exp,
                isLoggedIn: true,
                jti: token?.jti,
            };

            Object.entries(authData).forEach(([key, value]) => {
                storage.set(key, value);
            });

        } else {
            toast.error(response?.err || "Something went wrong");
            throw new Error('Token refresh failed');
        }

    }, [loginObj]);

    useEffect(() => {
        if (!isLoggedIn) return;

        const checkAndRefresh = async () => {
            const expiry = storage.get("access_exp");
            if (!expiry) return;

            const now = Math.floor(Date.now() / 1000);
            const timeLeft = expiry - now;

            console.log("Token check - time left (sec):", timeLeft);

            if (timeLeft <= 30) {
                console.log("Token expiring soon or expired, refreshing...");
                try {
                    await getRefreshToken();
                } catch (err) {
                    console.error("Silent refresh failed:", err);
                    // doLogout();
                }
            }
        };

        checkAndRefresh();

        const intervalId = setInterval(checkAndRefresh, 5000);

        return () => clearInterval(intervalId);

    }, [isLoggedIn, getRefreshToken]);

    if (!deviceId || deviceId === 'undefined') {
        getDeviceId();
    }

    const updateUserData = (key, value) => {
        setUserData((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const loginByPassword = async () => {
        if (!userData.email) {
            toast.error("Email is required");
            return;
        }

        if (!isValidEmail(userData.email.trim())) {
            toast.error("Please enter a valid email address");
            return;
        }

        if (!userData.password) {
            toast.error('Password is required');
            return;
        }

        const fcmToken = await requestForToken();

        const payload = {
            username: userData?.email,
            password: userData?.password,
            user_type: 'employee',
            source: "mobile",
            ...(fcmToken ? { fcm_token: fcmToken } : {}),
        };

        try {
            const response = await postApi(urls.loginByPassword, payload, false);
            if (response?.status === true) {
                const token = response?.jwt;

                const authData = {
                    accessToken: token?.access,
                    refreshToken: token?.refresh,
                    refresh_exp: token?.refresh_exp,
                    access_exp: token?.access_exp,
                    user: response?.user,
                    isLoggedIn: true,
                    jti: token?.jti,
                };

                Object.entries(authData).forEach(([key, value]) => {
                    storage.set(key, value);
                });
                storage.set('user_pid', authData.user?.user_pid);

                updateUserData('email', '');
                updateUserData('password', '');
                navigate('/home');
                getUserProfile();
                setTimeout(heartBeat, 2500);
            } else {
                toast.error(response?.err || response?.error || 'Something went wrong');
            }
        } catch (error) {
            let msg = "Unexpected error occurred";

            try {
                const res = error?.response?.data || JSON.parse(error?.responseText || '{}');
                msg = res?.err || res?.error || res?.message || msg;
            } catch (_) { }

            updateUserData('email', '');
            updateUserData('password', '');
            toast.error(msg);
        }
    };

    // Sends an OTP to the user's email via /auth/forget-password.
    // Options let the same endpoint back the change-password, forgot-password
    // and delete-account flows (they only differ by module + where the sign is stored).
    const userEmailVerify = async (options = {}) => {
        const {
            module = 'change-pass',
            signKey = 'change_pw_sign',
            emailOverride = null,
        } = options;
        const isDelete = module === 'delete-account';

        if (isLoggedIn) {
            const email = (emailOverride || storage.get('user')?.email)?.trim();
            if (!email) { toast.error('User email not found'); return false; }
            try {
                const response = await postApi(urls.userEmailVerify, { username: email, module, source: "mobile" }, false);
                if (response?.status === true) {
                    storage.set(signKey, response.sign);
                    toast.success(response?.toast || 'Verification code sent to your email');
                    return true;
                }
                toast.error(response?.err || 'Something went wrong');
                return false;
            } catch {
                toast.error('Unexpected error occurred');
                return false;
            }
        }

        const email = (emailOverride || userData.email)?.trim();
        if (!email) { toast.error("Please enter an email address"); return; }
        if (!isValidEmail(email)) { toast.error("Please enter a valid email address"); return; }

        try {
            const payload = { username: email, source: "mobile" };
            if (isDelete) payload.module = module;
            const response = await postApi(urls.userEmailVerify, payload, true);
            if (response?.status === true) {
                toast.success(response?.toast);
                storage.set(isDelete ? signKey : 'sign', response.sign);
                if (!isDelete) {
                    storage.set('verifiedEmail', email);
                    navigate('/reset-pass');
                    updateUserData('email', '');
                }
                return true;
            }
            toast.error(response.err || 'Something went wrong');
            if (!isDelete) updateUserData('email', '');
            return false;
        } catch (error) {
            toast.error(error?.err || "Unexpected error occurred");
            if (!isDelete) updateUserData('email', '');
            return false;
        }
    };

    const setPassword = async (overrides = null) => {
        const otp = (overrides?.otp ?? userData.otp)?.trim();
        const password = (overrides?.password ?? userData.setPassword)?.trim();
        const confirm_password = (overrides?.confirmPassword ?? userData.confirmPassword)?.trim();

        if (!otp) {
            toast.error('OTP is required');
            return false;
        }
        if (!password || !confirm_password) {
            toast.error("Password and Confirm Password are required");
            return false;
        }
        if (password !== confirm_password) {
            toast.error("Password and Confirm Password do not match");
            return false;
        }

        if (isLoggedIn) {
            try {
                const response = await postApi(urls.setPassword, {
                    otp, sign: storage.get('change_pw_sign'), password, confirm_password,
                    username: storage.get('user')?.email, module: 'change-pass', source: "mobile"
                });
                if (response?.status === true) {
                    toast.success(response?.msg || 'Password changed successfully');
                    storage.remove('change_pw_sign');
                    return true;
                }
                toast.error(response?.err || 'Something went wrong');
                return false;
            } catch {
                toast.error('Unexpected error occurred');
                return false;
            }
        }

        try {
            const response = await postApi(urls.setPassword, {
                otp, sign: storage.get('sign'), password, confirm_password,
                username: storage.get('verifiedEmail'),
                source: "mobile"
            });
            if (response?.status === true) {
                toast.success(response?.msg || "Password set successfully");
                const jwt = response?.login?.jwt;
                const authData = {
                    accessToken: jwt?.access,
                    refreshToken: jwt?.refresh,
                    refresh_exp: jwt?.refresh_exp,
                    access_exp: jwt?.access_exp,
                    user: response?.login?.user,
                    isLoggedIn: true,
                    jti: jwt?.jti,
                };

                Object.entries(authData).forEach(([key, value]) => {
                    storage.set(key, value);
                });
                storage.set('user_pid', authData.user?.user_pid);

                ["sign", "verifiedEmail"].forEach((key) => {
                    storage.remove(key);
                });

                updateUserData("otp", '');
                updateUserData("setPassword", '');
                updateUserData("confirmPassword", '');
                navigate('/home');
                getUserProfile();
            } else {
                toast.error(response?.err || "Something went wrong");
                updateUserData("otp", '');
                updateUserData("confirmPassword", '');
            }
        } catch (error) {
            console.error("setPassword error:", error);
            toast.error(error?.err || "Unexpected error occurred");
        }
    };

    const uploadProfilePic = async (base64String) => {
        const payload = {
            company_pid: appconfig.company_pid,
            entity_id: loginObj?.user_pid,
            entity_type: "Profile",
            media_slug: "user-profile",
            base64: base64String
        }

        try {
            const response = await postApi(urls.uploadMedia, payload);
            if (response?.status) {
                const storedUser = storage.get("user");
                const updatedUser = {
                    ...storedUser,
                    profile_img: response?.img_url
                };

                storage.set("user", updatedUser);

                toast.success(response?.toast || "Unexpected error occurred");
            }
        } catch (error) {
            console.error("uploadMedia error:", error);
            toast.error(error?.err || "Unexpected error occurred");
        }
    }

    const getUserProfile = useCallback(async () => {
        try {
            const response = await postApi(urls.getProfileDetails, {}, false, true);

            if (response?.status) {
                const data = response.data;

                setProfileData({
                    firstName: data.first_name || "",
                    lastName: data.last_name || "",
                    email: data.email || "",
                    phone: data.phone || "",
                    dob: data.dob || "",
                    gender: data.gender || "",
                });

                if (data.profile_img) {
                    setPreview(data.profile_img);
                }

                const cachedUser = storage.get("user") || {};
                storage.set("user", {
                    ...cachedUser,
                    first_name: data.first_name ?? cachedUser.first_name,
                    last_name: data.last_name ?? cachedUser.last_name,
                    email: data.email ?? cachedUser.email,
                    phone: data.phone ?? cachedUser.phone,
                    dob: data.dob ?? cachedUser.dob,
                    gender: data.gender ?? cachedUser.gender,
                    profile_img: data.profile_img ?? cachedUser.profile_img,
                    ...(data.is_push_notification_enable !== undefined && {
                        is_push_notification_enable: data.is_push_notification_enable,
                    }),
                });
            }

        } catch (error) {
            console.error("getUserProfile error:", error);
            toast.error(error?.err || "Unexpected error occurred");
        }
    }, [setProfileData, setPreview]);

    const updateProfile = useCallback(async () => {
        try {
            let formattedDob = "";

            if (profileData.dob) {
                if (profileData.dob.includes("/")) {
                    const [dd, mm, yyyy] = profileData.dob.split("/");
                    formattedDob = `${yyyy}-${mm}-${dd}`;
                } else if (profileData.dob.includes("-")) {
                    const parts = profileData.dob.split("-");

                    if (parts[0].length === 4) {
                        formattedDob = profileData.dob;
                    } else {
                        const [dd, mm, yyyy] = parts;
                        formattedDob = `${yyyy}-${mm}-${dd}`;
                    }
                }
            }

            const payload = {
                company_pid: appconfig.company_pid,
                first_name: profileData.firstName,
                last_name: profileData.lastName,
                phone: profileData.phone,
                profile_img_pid: preview,
                dob: formattedDob,
                gender: profileData.gender,
                source: "mobile"
            };

            if (!payload.first_name) return toast.error("Please enter your first name.");
            if (!payload.last_name) return toast.error("Please enter your last name.");
            if (!payload.phone) return toast.error("Please enter your phone number.");
            if (!payload.dob) return toast.error("Please select your date of birth.");
            if (!payload.gender) return toast.error("Please select your gender.");

            const res = await postApi(urls.updateUserProfile, payload);

            if (res.status) {
                toast.success(res.msg);
                storage.set("user", res.user);
            } else {
                toast.error(res.error);
            }

        } catch (error) {
            console.error(error);
            toast.error("Something went wrong");
        }
    }, [profileData, preview]);

    const getContent = useCallback(async (slug) => {
        const payload = {
            company_pid: appconfig.company_pid,
            slug
        };

        try {
            const response = await postApi(urls.getCms, payload, true);

            if (response?.status) {
                setContent(response?.data)
            } else {
                toast.error(response.error);
                return null;
            }

        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);


    const getChatList = useCallback(async (search = "") => {
        const payload = {
            search
        };

        try {
            const response = await postApi(urls.chatList, payload, false, true);

            if (response?.status) {
                setChatList(response?.chat_management_list)
            } else {
                toast.error(response.error);
                return null;
            }

        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    const getEmployeeList = useCallback(async (search = "") => {
        const payload = {
            search,
            company_pid: appconfig.company_pid,
            user_type: 'Employee',
            user_status: 'active',
        };

        try {
            const response = await postApi(urls.employeeList, payload, false, true);

            if (response?.status) {
                setEmployeeList(response?.userList || []);
            } else {
                toast.error(response.error);
                return null;
            }

        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    const createChatRoom = useCallback(async (user_pid) => {
        const payload = {
            user_pid: user_pid,
            company_pid: appconfig.company_pid,
        };

        try {
            const response = await postApi(urls.createChatRoom, payload, false, true);
            if (response?.status) {
                return response?.chat_room;
            } else {
                toast.error(response.error);
                return null;
            }

        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    const sendMessages = useCallback(async ({
        room_pid,
        msg,
        msg_type = "text",
        file_base64 = [],
    }) => {
        const payload = {
            room_pid,
            msg_type,
            msg,
            file_base64: Array.isArray(file_base64)
                ? file_base64
                : [file_base64],
            company_pid: appconfig.company_pid,
        };

        try {
            const response = await postApi(urls.sendMessage, payload, false, true);

            if (response?.status) {
                return response.insert;
            } else {
                toast.error(response.error);
                return null;
            }
        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    const getMessages = useCallback(async ({ room_pid, }) => {
        const payload = {
            room_pid,
            company_pid: appconfig.company_pid,
        };

        try {
            const response = await postApi(urls.getChatMessages, payload, false, true);

            if (response?.status) {
                return response;
            } else {
                toast.error(response.error);
                return null;
            }
        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    const getActivityList = useCallback(async () => {
        const payload = {
            company_pid: appconfig.company_pid,
        };

        try {
            const response = await postApi(urls.getActivityList, payload, false, true);
            if (response?.status) {
                return response.activity_list;
            } else {
                toast.error(response.error);
                return null;
            }
        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    const createManualLog = useCallback(async (data) => {
        const payload = {
            activity_date: data.date,
            from_time: data.fromTime,
            to_time: data.toTime,
            company_pid: appconfig.company_pid,
            job_pid: data.jobPid || "",
            employee_pid: storage.get("user")?.user_pid,
            title: data.title || "",
            remark: data.remark || "",
        };

        try {
            const response = await postApi(urls.createManualLog, payload, false, true);

            if (response?.status) {
                return response.insert;
            } else {
                toast.error(response.error);
                return null;
            }
        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    // Fetches the employee's pending jobs for a given date (defaults to today).
    // Used by the Log Manual Time modal to preload jobs when it opens / date changes.
    const getPendingJobDetails = useCallback(async (date) => {
        const payload = {
            employee_pid: storage.get("user")?.user_pid,
            date: date || new Date().toLocaleDateString("en-CA"),
        };

        try {
            const response = await postApi(urls.getPendingJobDetails, payload, false, true);
            return response;
        } catch (error) {
            console.error("getPendingJobDetails error:", error);
            return null;
        }
    }, []);

    const startJob = useCallback(async (job_pid, estimatedMinutes) => {
        try {
            const payload = { job_pid };
            if (estimatedMinutes != null) payload.estimated_minutes = estimatedMinutes;
            const response = await postApi(urls.startJob, payload);
            if (response?.status) {
                toast.success(response?.msg);
                return true;
            } else {
                toast.error(response?.error || "Failed to start job");
                return false;

            }
        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return false;
        }
    }, []);

    const uploadWorkPhoto = useCallback(async ({ job_pid, photos }) => {
        const payload = {
            job_pid,
            user_pid: loginObj?.user_pid,
            photos,
        };

        try {
            const response = await postApi(urls.uploadWorkPhoto, payload);
            if (response?.status) {
                toast.success(response?.msg);
                return true;
            } else {
                toast.error(response?.error || "Failed to upload photo");
                return false;
            }
        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return false;
        }
    }, [loginObj]);

    const saveDamageNotes = useCallback(async ({ job_pid, notes, checklist }) => {
        const payload = {
            job_pid,
            user_pid: loginObj?.user_pid,
            notes,
            checklist,
        };

        try {
            const response = await postApi(urls.saveDamageNotes, payload);
            if (response?.status) {
                toast.success("work has been saved successfully" || response?.msg);
                return true;
            } else {
                toast.error(response?.error || "Failed to save notes");
                return false;
            }
        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return false;
        }
    }, [loginObj]);

    const getJobDetail = useCallback(async (job_pid) => {
        try {
            const response = await postApi(urls.jobDetail, { job_pid });
            if (response?.status) {
                setJobDetail(response.data);
            } else {
                toast.error(response?.error || "Failed to get job detail");
            }
        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
        }
    }, []);

    const markCompleted = useCallback(async ({ module_pid, time_spent }) => {
        try {
            const response = await postApi(urls.markCompleted, { module_pid, time_spent }, false, true);
            if (response?.status) {
                return response;
            } else {
                toast.error(response?.error || "Failed to mark as completed");
                return null;
            }
        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    const submitTest = useCallback(async ({ test_pid, answers }) => {
        try {
            const response = await postApi(urls.submitTest, { test_pid, answers });
            if (response?.status) {
                return response;
            } else {
                toast.error(response?.error || "Failed to submit test");
                return null;
            }
        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    const getModuleDetail = useCallback(async (module_pid) => {
        let payload = {
            module_pid,
            platform: 'App'
        }
        try {
            const response = await postApi(urls.getModuleDetail, payload, false, false);
            if (response?.status) {
                return response;
            } else {
                toast.error(response?.error || "Failed to get module detail");
                return null;
            }
        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    const getCourseChapters = useCallback(async (course_pid) => {
        const payload = { course_pid };

        try {
            const response = await postApi(urls.getCourseChapters, payload, false, false);
            if (response?.status) {
                return response;
            } else {
                toast.error(response?.error || "Failed to get course chapters");
                return null;
            }
        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    const getCourseList = useCallback(async (search = "") => {
        const payload = {
            company_pid: appconfig.company_pid,
            search,
            status: 'active',
            platform: 'app'
        };

        try {
            const response = await postApi(urls.getCourseList, payload, false, true);
            if (response?.status) {
                setCourseList(response?.course_list || []);
            } else {
                toast.error(response?.error || "Failed to get course list");
            }
        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
        }
    }, []);

    const getJobsList = useCallback(async ({ status, page = 1, per_page } = {}) => {
        const emptyPagination = { page: 1, per_page: per_page || 12, total: 0, total_pages: 1 };
        try {
            const payload = { status, page };
            if (per_page) payload.per_page = per_page;
            const response = await postApi(urls.jobList, payload, false, true);

            if (response?.status) {
                const data = response.data || [];
                const pagination = response.pagination || emptyPagination;
                setJobsList(data);
                return { data, pagination };
            }
            toast.error(response?.error || "Failed to get jobs list");
        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
        }
        return { data: [], pagination: emptyPagination };
    }, []);

    const getServiceList = useCallback(async (job_pid) => {
        const payload = {
            company_pid: appconfig.company_pid,
            job_pid: job_pid
        };

        try {
            const response = await postApi(urls.getServiceList, payload);

            if (response?.status) {
                return response?.data || [];

            } else {
                toast.error(response.error);
                return null;
            }

        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    const getServiceDetails = useCallback(async (service_pid, job_pid) => {
        const payload = {
            company_pid: appconfig.company_pid,
            service_pid: service_pid,
            job_pid: job_pid
        };

        try {
            const response = await postApi(urls.getServiceDetail, payload);

            if (response?.status) {
                return response?.data || [];

            } else {
                toast.error(response.error);
                return null;
            }

        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    const submitServiceReport = useCallback(async (serviceData) => {
        const payload = {
            company_pid: appconfig.company_pid,
            ...serviceData
        };
        try {
            const response = await postApi(urls.submitServiceReport, payload);
            if (response?.status) {
                toast.success(response?.msg || "Service report submitted successfully");
                return response;
            } else {
                toast.error(response.error);
                return null;
            }

        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    const sendAssessmentReport = useCallback(async (job_pid) => {
        const payload = {
            job_pid: job_pid
        };
        try {
            const response = await postApi(urls.sendAssessmentReport, payload);
            if (response?.status) {
                toast.success(response?.msg || "Report sent successfully");
                return response;
            } else {
                toast.error(response.error);
                return null;
            }

        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    const getPointSummary = useCallback(async () => {
        const payload = {
            company_pid: appconfig.company_pid,
        };

        try {
            const response = await postApi(urls.getPointSummary, payload);

            if (response?.status) {
                return response?.data || {};

            } else {
                toast.error(response.error);
                return null;
            }

        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    const getRewardsList = useCallback(async (search = '') => {
        const payload = {
            search,
            status: '',
            company_pid: appconfig.company_pid,
        };

        try {
            // const response = await postApi(urls.getRewardsList, payload);
            const response = await postApi(
                urls.getRewardsList,
                payload,
                false,
                !!search
            );
            if (response?.status) {
                return response?.productList?.products || [];

            } else {
                toast.error(response.error);
                return null;
            }

        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    const getRewardDetails = useCallback(async (product_pid) => {
        const payload = {
            product_id: product_pid,
            company_pid: appconfig.company_pid,
        };

        try {
            const response = await postApi(urls.getRewardDetails, payload);

            if (response?.status) {
                return response?.data || {};

            } else {
                toast.error(response.error);
                return null;
            }

        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    const redeemReward = useCallback(async (product_pid) => {
        const payload = {
            product_pid: product_pid,
            company_pid: appconfig.company_pid,
        };

        try {
            const response = await postApi(urls.redeemReward, payload);

            if (response?.status) {
                toast.success(response?.msg || "Reward redeemed successfully");
                return response || {};

            } else {
                toast.error(response.error);
                return null;
            }

        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    const leaderBoard = useCallback(async (page = 1) => {
        const payload = {
            company_pid: appconfig.company_pid,
            page,
        };

        try {
            const response = await postApi(urls.leaderBoard, payload);
            if (response?.status) {
                return response?.data || {};
            } else {
                toast.error(response.error);
                return null;
            }
        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    const getPointHistory = useCallback(async () => {
        const payload = {
            company_pid: appconfig.company_pid,
        };

        try {
            const response = await postApi(urls.pointHistory, payload);
            if (response?.status) {
                return response?.data || {};
            } else {
                toast.error(response.error);
                return null;
            }

        } catch (error) {
            toast.error(error?.error || "Unexpected error occurred");
            return null;
        }
    }, []);

    const getSalesStats = useCallback(async (filter = { type: "preset", preset: "this_month" }) => {
        const toStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        let start_date, end_date;

        if (filter.type === "preset") {
            switch (filter.preset) {
                case "today":
                    start_date = end_date = toStr(today);
                    break;
                case "yesterday": {
                    const y = new Date(today); y.setDate(today.getDate() - 1);
                    start_date = end_date = toStr(y);
                    break;
                }
                case "last_7_days": {
                    const s = new Date(today); s.setDate(today.getDate() - 6);
                    start_date = toStr(s); end_date = toStr(today);
                    break;
                }
                case "last_30_days": {
                    const s = new Date(today); s.setDate(today.getDate() - 29);
                    start_date = toStr(s); end_date = toStr(today);
                    break;
                }
                case "this_month": {
                    const s = new Date(now.getFullYear(), now.getMonth(), 1);
                    start_date = toStr(s); end_date = toStr(today);
                    break;
                }
                case "last_week": {
                    const s = new Date(today); s.setDate(today.getDate() - 7);
                    const e = new Date(today); e.setDate(today.getDate() - 1);
                    start_date = toStr(s); end_date = toStr(e);
                    break;
                }
                case "last_month": {
                    const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                    const e = new Date(now.getFullYear(), now.getMonth(), 0);
                    start_date = toStr(s); end_date = toStr(e);
                    break;
                }
                case "last_year": {
                    const y = now.getFullYear() - 1;
                    start_date = `${y}-01-01`; end_date = `${y}-12-31`;
                    break;
                }
                default:
                    start_date = end_date = toStr(today);
            }
        } else if (filter.type === "single") {
            start_date = end_date = filter.date;
        } else {
            start_date = filter.start;
            end_date = filter.end;
        }

        try {
            const response = await postApi(urls.salesStats, {
                company_pid: appconfig.company_pid,
                employee_pid: loginObj?.user_pid,
                start_date,
                end_date,
            }, false, true);
            return response?.status ? (response?.data || {}) : {};
        } catch {
            return {};
        }
    }, [loginObj]);

    const heartBeat = useCallback(async () => {
        console.log("HeartBeat triggered");
        const currentUser = storage.get("user");
        if (!storage.get("isLoggedIn") || !currentUser?.user_pid) return;
        console.log("Sending heartbeat for user_pid:", currentUser.user_pid);
        try {
            const response = await postApi(urls.heartBeat, {
                company_pid: appconfig.company_pid,
                user_pid: currentUser?.user_pid,
            }, false, true);
            if (response?.status) {
                setUnreadMessages(response?.unread_messages || 0);
            }
        } catch { /* silent — background ping */ }
    }, []);

    const toggleNotification = useCallback(async (enable) => {
        try {
            const payload = {
                company_pid: appconfig.company_pid,
                is_push_notification_enable: enable ? 1 : 0,
            };
            const response = await postApi(urls.toggleNotification, payload);
            if (response?.status === true) {
                const user = storage.get("user");
                if (user) {
                    storage.set("user", {
                        ...user,
                        is_push_notification_enable: enable ? 1 : 0,
                    });
                }
                storage.set("notificationsEnabled", enable);
                toast.success(
                    response?.msg ||
                    `Notifications ${enable ? "enabled" : "disabled"}`
                );
                return true;
            }
            toast.error(
                response?.err ||
                response?.error ||
                "Failed to update notification preference"
            );
            return false;
        } catch (error) {
            toast.error(error?.message || "Unexpected error occurred");
            return false;
        }
    }, []);

    useEffect(() => {
        const id = setInterval(() => {
            if (storage.get("isLoggedIn")) heartBeat();
        }, 2 * 60 * 1000);
        // }, 3000);
        return () => clearInterval(id);
    }, [heartBeat]);

    useEffect(() => {
        if (storage.get("isLoggedIn")) getUserProfile();
    }, [getUserProfile]);

    const clearProfile = useCallback(() => {
        setProfileData({
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            dob: "",
            gender: "",
        });
        setPreview(images.userAvatar);
        setJobsList([]);
        setJobDetail(null);
        setChatList([]);
        setEmployeeList([]);
        setCourseList([]);
        setContent({});
        setUnreadMessages(0);
    }, []);

    const doLogout = useCallback(async () => {
        const currentUser = storage.get("user");
        try {
            await postApi(urls.logout, {
                company_pid: appconfig.company_pid,
                user_pid: currentUser?.user_pid,
            }, false, true);
        } catch { /* ignore API failure — proceed with local logout regardless */ }

        // clears localStorage but preserves device_id
        await storage.clear();
        clearProfile();
    }, [clearProfile]);

    // Step 2 — verify the OTP via /auth/verify-otp.
    // Logged-in (profile) users are identified by device_pid + username (email).
    // The public delete page (no session) is identified by email (username) + sign.
    const deleteAccountVerifyOtp = useCallback(async (otp, emailOverride) => {
        const loggedIn = storage.get("isLoggedIn");
        const code = otp?.trim();
        if (!code || code.length < 6) { toast.error("Please enter the 6-digit code"); return false; }

        const payload = loggedIn
            ? {
                device_pid: storage.get("device_id"),
                username: storage.get("user")?.email,
                otp: code,
                source: "mobile",
            }
            : {
                username: (emailOverride || "").trim(),
                otp: code,
                sign: storage.get("delete_acc_sign"),
                module: "delete-account",
                source: "mobile",
            };

        try {
            const response = await postApi(urls.deleteAccountVerifyOtp, payload, !loggedIn);
            if (response?.status === true) {
                toast.success(response?.toast || response?.msg || "OTP verified");
                return true;
            }
            toast.error(response?.err || response?.error || "Invalid OTP");
            return false;
        } catch {
            toast.error("Unexpected error occurred");
            return false;
        }
    }, []);

    // Step 3 — permanently delete the account via /auth/delete-account.
    // Same split: session users go by username (email); the public page goes by email + sign.
    const deleteAccount = useCallback(async (otp, emailOverride) => {
        const loggedIn = storage.get("isLoggedIn");

        const payload = loggedIn
            ? {
                device_pid: storage.get("device_id"),
                username: storage.get("user")?.email,
                company_pid: appconfig.company_pid,
                source: "mobile",
            }
            : {
                username: (emailOverride || "").trim(),
                otp: otp?.trim(),
                sign: storage.get("delete_acc_sign"),
                company_pid: appconfig.company_pid,
                module: "delete-account",
                source: "mobile",
            };

        try {
            const response = await postApi(urls.deleteAccount, payload, !loggedIn);

            if (response?.status === true) {
                toast.success(response?.msg || "Your account has been deleted");
                storage.remove("delete_acc_sign");
                // Only wipe the local session when a logged-in user deletes their own account.
                if (loggedIn) {
                    await storage.clear();
                    clearProfile();
                }
                return true;
            }

            toast.error(response?.err || response?.error || "Failed to delete account");
            return false;
        } catch (error) {
            toast.error(error?.message || "Unexpected error occurred");
            return false;
        }
    }, [clearProfile]);


    return (
        <GlobalContext.Provider
            value={{
                isLoading,
                loginByPassword,
                userData,
                updateUserData,
                userEmailVerify,
                setPassword,
                uploadProfilePic,
                profileData,
                setProfileData,
                preview,
                setPreview,
                getUserProfile,
                updateProfile,
                getContent,
                content,
                chatList,
                getChatList,
                employeeList,
                getEmployeeList,
                createChatRoom,
                sendMessages,
                getMessages,
                getActivityList,
                createManualLog,
                getPendingJobDetails,
                jobsList,
                getJobsList,
                jobDetail,
                getJobDetail,
                startJob,
                saveDamageNotes,
                uploadWorkPhoto,
                courseList,
                getCourseList,
                getCourseChapters,
                getModuleDetail,
                submitTest,
                markCompleted,
                getServiceList,
                sendAssessmentReport,
                getServiceDetails,
                submitServiceReport,
                getRewardsList,
                getPointSummary,
                getRewardDetails,
                redeemReward,
                leaderBoard,
                getPointHistory,
                getSalesStats,
                heartBeat,
                unreadMessages,
                toggleNotification,
                clearProfile,
                doLogout,
                deleteAccountVerifyOtp,
                deleteAccount
            }}
        >
            {isLoading && (
                <div style={styles.loaderWrapper}>
                    <Oval
                        height={44}
                        width={44}
                        color="var(--primaryColor)"
                        secondaryColor="rgba(255,255,255,0.6)"
                        strokeWidth={5}
                    />
                </div>
            )}
            {children}
        </GlobalContext.Provider>
    );
};
const styles = {
    loaderWrapper: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "rgba(0,0,0,0.6)",
        zIndex: 9999,
    },
};
export default GlobalProvider;
export const useGlobal = () => useContext(GlobalContext);